import React from "react";
import { RouteObject } from "react-router-dom";

import {
  checkAnyScope,
  checkBooleanScope,
} from "@src/components/ScopeWrappers/helpers";

import { EXPERIMENT_ROUTES } from "../../routes";

export const ExperimentRoutes: RouteObject[] = [
  {
    path: EXPERIMENT_ROUTES.Experiences,
    lazy: async () => {
      const { ExperiencesHomeRoute } = await import(
        "../../../features/experienceTests/pages/home/ExperiencesHome.route"
      );
      return { element: <ExperiencesHomeRoute /> };
    },
  },
  {
    path: EXPERIMENT_ROUTES.PriceTest,
    lazy: async () => {
      const { PriceExperienceRoute } = await import(
        "../../../features/experienceTests/pages/priceExperience/PriceExperience.route"
      );
      const { ScopedRoute } = await import(
        "../../../components/ScopeWrappers/ScopedRoute"
      );
      return {
        element: (
          <ScopedRoute
            debugId={EXPERIMENT_ROUTES.PriceTest}
            isEnabled={(org) =>
              checkAnyScope([
                checkBooleanScope("view_price_experiment", org.configurations),
                checkBooleanScope(
                  "create_price_experiment",
                  org.configurations
                ),
              ])
            }
            children={PriceExperienceRoute}
          />
        ),
      };
    },
  },
  {
    path: EXPERIMENT_ROUTES.PriceTest_Id,
    lazy: async () => {
      const { PriceExperienceRoute } = await import(
        "../../../features/experienceTests/pages/priceExperience/PriceExperience.route"
      );
      const { ScopedRoute } = await import(
        "../../../components/ScopeWrappers/ScopedRoute"
      );
      return {
        element: (
          <ScopedRoute
            debugId={EXPERIMENT_ROUTES.PriceTest_Id}
            isEnabled={(org) =>
              checkAnyScope([
                checkBooleanScope("view_price_experiment", org.configurations),
                checkBooleanScope("edit_price_experiment", org.configurations),
              ])
            }
            children={PriceExperienceRoute}
          />
        ),
      };
    },
  },
  {
    path: EXPERIMENT_ROUTES.DuplicateProducts_Id,
    lazy: async () => {
      const { DuplicateProductsRoute } = await import(
        "../../../features/duplicateProducts/duplicateProducts.route"
      );
      const { ScopedRoute } = await import(
        "../../../components/ScopeWrappers/ScopedRoute"
      );
      return {
        element: (
          <ScopedRoute
            debugId={EXPERIMENT_ROUTES.DuplicateProducts_Id}
            isEnabled={(org) =>
              checkAnyScope([
                checkBooleanScope("view_price_experiment", org.configurations),
                checkBooleanScope(
                  "create_price_experiment",
                  org.configurations
                ),
              ])
            }
            children={DuplicateProductsRoute}
          />
        ),
      };
    },
  },
  {
    path: EXPERIMENT_ROUTES.ShippingProfiles_Id,
    lazy: async () => {
      const { ShippingBackupProfiles } = await import(
        "../../../features/experienceTests/pages/shippingExperience/components/ShippingBackupProfiles"
      );
      const { ScopedRoute } = await import(
        "../../../components/ScopeWrappers/ScopedRoute"
      );
      return {
        element: (
          <ScopedRoute
            debugId={EXPERIMENT_ROUTES.ShippingProfiles_Id}
            isEnabled={(org, isAdmin) => isAdmin}
            children={ShippingBackupProfiles}
          />
        ),
      };
    },
  },
  {
    path: EXPERIMENT_ROUTES.ContentTest,
    lazy: async () => {
      const { ContentExperienceRoute } = await import(
        "../../../features/experienceTests/pages/contentExperience/ContentExperience.route"
      );
      const { ScopedRoute } = await import(
        "../../../components/ScopeWrappers/ScopedRoute"
      );
      return {
        element: (
          <ScopedRoute
            debugId={EXPERIMENT_ROUTES.ContentTest}
            isEnabled={(org) =>
              checkAnyScope([
                checkBooleanScope(
                  "create_content_experiment",
                  org.configurations
                ),
                checkBooleanScope(
                  "view_content_experiment",
                  org.configurations
                ),
              ])
            }
            children={ContentExperienceRoute}
          />
        ),
      };
    },
  },
  {
    path: EXPERIMENT_ROUTES.ContentTest_Id,
    lazy: async () => {
      const { ContentExperienceRoute } = await import(
        "../../../features/experienceTests/pages/contentExperience/ContentExperience.route"
      );
      const { ScopedRoute } = await import(
        "../../../components/ScopeWrappers/ScopedRoute"
      );
      return {
        element: (
          <ScopedRoute
            debugId={EXPERIMENT_ROUTES.ContentTest_Id}
            isEnabled={(org) =>
              checkAnyScope([
                checkBooleanScope(
                  "edit_content_experiment",
                  org.configurations
                ),
                checkBooleanScope(
                  "view_content_experiment",
                  org.configurations
                ),
              ])
            }
            children={ContentExperienceRoute}
          />
        ),
      };
    },
  },
  {
    path: EXPERIMENT_ROUTES.ShippingTest,
    lazy: async () => {
      const { ShippingExperienceRoute } = await import(
        "../../../features/experienceTests/pages/shippingExperience/ShippingExperience.route"
      );
      const { ScopedRoute } = await import(
        "../../../components/ScopeWrappers/ScopedRoute"
      );
      return {
        element: (
          <ScopedRoute
            debugId={EXPERIMENT_ROUTES.ShippingTest}
            isEnabled={(org) =>
              checkAnyScope([
                checkBooleanScope(
                  "view_shipping_experiment",
                  org.configurations
                ),
                checkBooleanScope(
                  "create_shipping_experiment",
                  org.configurations
                ),
              ])
            }
            children={ShippingExperienceRoute}
          />
        ),
      };
    },
  },
  {
    path: EXPERIMENT_ROUTES.ShippingTest_Id,
    lazy: async () => {
      const { ShippingExperienceRoute } = await import(
        "../../../features/experienceTests/pages/shippingExperience/ShippingExperience.route"
      );
      const { ScopedRoute } = await import(
        "../../../components/ScopeWrappers/ScopedRoute"
      );
      return {
        element: (
          <ScopedRoute
            debugId={EXPERIMENT_ROUTES.ShippingTest_Id}
            isEnabled={(org) =>
              checkAnyScope([
                checkBooleanScope(
                  "edit_shipping_experiment",
                  org.configurations
                ),
                checkBooleanScope(
                  "view_shipping_experiment",
                  org.configurations
                ),
              ])
            }
            children={ShippingExperienceRoute}
          />
        ),
      };
    },
  },
  {
    path: EXPERIMENT_ROUTES.Compare_Id,
    lazy: async () => {
      const { VariationCompareRoute } = await import(
        "../../../features/experienceTests/pages/variationCompare/VariationCompare.route"
      );
      return { element: <VariationCompareRoute /> };
    },
  },
  {
    path: EXPERIMENT_ROUTES.OfferTest,
    lazy: async () => {
      const { OfferExperienceRoute } = await import(
        "../../../features/experienceTests/pages/offerExperience/OfferExperience.route"
      );
      const { ScopedRoute } = await import(
        "../../../components/ScopeWrappers/ScopedRoute"
      );
      return {
        element: (
          <ScopedRoute
            debugId={EXPERIMENT_ROUTES.OfferTest_Id}
            isEnabled={(org, isAdmin) =>
              checkAnyScope([
                checkBooleanScope(
                  "create_discount_experiment",
                  org.configurations
                ),
                checkBooleanScope(
                  "view_discount_experiment",
                  org.configurations
                ),
                isAdmin,
              ])
            }
            children={OfferExperienceRoute}
          />
        ),
      };
    },
  },
  {
    path: EXPERIMENT_ROUTES.OfferTest_Id,
    lazy: async () => {
      const { OfferExperienceRoute } = await import(
        "../../../features/experienceTests/pages/offerExperience/OfferExperience.route"
      );
      const { ScopedRoute } = await import(
        "../../../components/ScopeWrappers/ScopedRoute"
      );
      return {
        element: (
          <ScopedRoute
            debugId={EXPERIMENT_ROUTES.OfferTest_Id}
            isEnabled={(org) =>
              checkAnyScope([
                checkBooleanScope(
                  "edit_discount_experiment",
                  org.configurations
                ),
                checkBooleanScope(
                  "view_discount_experiment",
                  org.configurations
                ),
              ])
            }
            children={OfferExperienceRoute}
          />
        ),
      };
    },
  },
];
